import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { TiptapEditor, type TiptapEditorRef } from './TiptapEditor'
import {
    buildSignatureTable,
    readSignatureLayoutFromImg,
    SIGNATURE_LOGO_DEFAULT_MAX_WIDTH,
    SIGNATURE_LOGO_MAX_MAX_WIDTH,
    SIGNATURE_LOGO_MIN_MAX_WIDTH,
    type SignatureLogoPosition,
} from './utils'

export interface SignatureFooterEditorProps {
    content: string
    onChange: (html: string) => void
    placeholder?: string
    onFocus?: () => void
    minHeight?: number
    onImageUpload?: (file: File) => Promise<string>
}

export type SignatureFooterEditorRef = TiptapEditorRef

interface ParsedSignature {
    imageUrl: string | null
    text: string
    position: SignatureLogoPosition
    maxWidth: number
}

const DEFAULT_PARSED: ParsedSignature = {
    imageUrl: null,
    text: '',
    position: 'left',
    maxWidth: SIGNATURE_LOGO_DEFAULT_MAX_WIDTH,
}

const SIZE_KEY_STEP = 10
const SNAP_ANIMATION_MS = 220
// 6 px padding on .image-preview × 2 — added to the inline slot dimensions so
// the rendered image's content box is genuinely maxWidth × maxWidth.
const TILE_PADDING_TOTAL = 12

// Cycle order matched to the layout diagram: left → top → right → bottom.
const POSITION_CYCLE: ReadonlyArray<SignatureLogoPosition> = ['left', 'top', 'right', 'bottom']

// Parse combined signature HTML into image URL + text HTML + layout state.
const parseContent = (html: string): ParsedSignature => {
    if (!html || typeof window === 'undefined') return { ...DEFAULT_PARSED, text: html || '' }

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Case 1: signature table (new structured format)
    const container = doc.querySelector('table[data-signature-container]')
    if (container) {
        const img = container.querySelector('img[data-signature-logo]') as HTMLImageElement | null
        const textCell = container.querySelector('[data-signature-text]')
        const layout = readSignatureLayoutFromImg(img, container)
        return {
            imageUrl: img?.getAttribute('src') || null,
            text: textCell?.innerHTML || '',
            position: layout.position,
            maxWidth: layout.maxWidth,
        }
    }

    // Case 2: legacy format with a plain <img> embedded — extract it.
    // Defaults to the historical left/100px so the rendered output stays stable.
    const plainImg = doc.body.querySelector('img')
    if (plainImg) {
        const src = plainImg.getAttribute('src')
        plainImg.remove()
        return { ...DEFAULT_PARSED, imageUrl: src || null, text: doc.body.innerHTML.trim() }
    }

    // Case 3: no image — all content is text
    return { ...DEFAULT_PARSED, text: html }
}

// Build combined HTML using an email-safe table layout. Delegates to
// buildSignatureTable so URL validation + HTML escaping live in one place.
const buildContent = (imageUrl: string | null, text: string, position: SignatureLogoPosition, maxWidth: number): string => {
    if (!imageUrl) return text
    return buildSignatureTable(imageUrl, text, { position, maxWidth })
}

const UploadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5"
            stroke="#3354DF"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M14.1663 6.66667L9.99967 2.5L5.83301 6.66667" stroke="#3354DF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 2.5V12.5" stroke="#3354DF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const ResizeHandleIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path d="M9 1 1 9M9 5 5 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
)

// Three short gray bars representing signature text. Reused in both
// horizontal and stacked layouts of PositionDiagram.
const DiagramTextLines = () => (
    <g className="diagram-text">
        <rect width={18} height={2} rx={1} />
        <rect width={18} height={2} rx={1} y={5} />
        <rect width={14} height={2} rx={1} y={10} />
    </g>
)

// Read-only mini-illustration that mirrors the rendered signature layout
// (logo block + text lines), switching between side-by-side and stacked
// arrangements. Wrapped by a clickable button below — click cycles position.
const PositionDiagram = ({ position }: { position: SignatureLogoPosition }) => {
    const isHorizontal = position === 'left' || position === 'right'
    const logoFirst = position === 'left' || position === 'top'

    if (isHorizontal) {
        const logoX = logoFirst ? 6 : 34
        const textX = logoFirst ? 22 : 2
        return (
            <svg className="position-diagram-svg" width={52} height={22} viewBox="0 0 52 22" aria-hidden="true">
                <rect className="diagram-logo" x={logoX} y={6} width={12} height={10} rx={1.5} />
                <g transform={`translate(${textX} 4)`}>
                    <DiagramTextLines />
                </g>
            </svg>
        )
    }

    const logoY = logoFirst ? 2 : 16
    const textY = logoFirst ? 14 : 0
    return (
        <svg className="position-diagram-svg" width={28} height={28} viewBox="0 0 28 28" aria-hidden="true">
            <rect className="diagram-logo" x={8} y={logoY} width={12} height={10} rx={1.5} />
            <g transform={`translate(5 ${textY})`}>
                <DiagramTextLines />
            </g>
        </svg>
    )
}

const POSITION_LABELS: Record<SignatureLogoPosition, string> = {
    left: 'left',
    right: 'right',
    top: 'top',
    bottom: 'bottom',
}

const POSITION_LABEL_CAPITALIZED: Record<SignatureLogoPosition, string> = {
    left: 'Left',
    right: 'Right',
    top: 'Top',
    bottom: 'Bottom',
}

export const SignatureFooterEditor = forwardRef<SignatureFooterEditorRef, SignatureFooterEditorProps>(
    ({ content, onChange, placeholder, onFocus, minHeight, onImageUpload }, ref) => {
        const editorRef = useRef<TiptapEditorRef>(null)
        const fileInputRef = useRef<HTMLInputElement>(null)
        const columnRef = useRef<HTMLDivElement>(null)

        const [imageUrl, setImageUrl] = useState<string | null>(null)
        const [text, setText] = useState('')
        const [position, setPosition] = useState<SignatureLogoPosition>('left')
        const [maxWidth, setMaxWidth] = useState<number>(SIGNATURE_LOGO_DEFAULT_MAX_WIDTH)
        const [uploading, setUploading] = useState(false)

        // Direct-manipulation state — pared down: no body drag, no drop overlay,
        // no ghost. Position changes go through `cyclePosition` (diagram click),
        // resize still uses the corner grip.
        const [isLogoFocused, setIsLogoFocused] = useState(false)
        const [isResizingLogo, setIsResizingLogo] = useState(false)
        const [resizeBadgePos, setResizeBadgePos] = useState<{ x: number; y: number } | null>(null)
        const [justSnapped, setJustSnapped] = useState(false)

        const isInternalChangeRef = useRef(false)
        const lastContentRef = useRef('')
        const prevPositionRef = useRef<SignatureLogoPosition>(position)
        // The inner TiptapEditor fires a spurious onChange while it normalizes the seeded HTML at mount, BEFORE
        // any real edit. Treating that as a user edit pushed an empty signature up to the parent and cleared the
        // seeded signature + logo — only latched true once the text editor is actually focused.
        const textFocusedRef = useRef(false)

        // Sync external content changes into internal state
        useEffect(() => {
            if (isInternalChangeRef.current) {
                isInternalChangeRef.current = false
                lastContentRef.current = content
                return
            }
            if (content === lastContentRef.current) return

            const parsed = parseContent(content)
            setImageUrl(parsed.imageUrl)
            setText(parsed.text)
            setPosition(parsed.position)
            setMaxWidth(parsed.maxWidth)
            lastContentRef.current = content
        }, [content])

        // Brief snap animation whenever position changes (diagram click,
        // keyboard, or external content sync). Skipped on initial mount.
        useEffect(() => {
            if (prevPositionRef.current === position) return
            prevPositionRef.current = position
            setJustSnapped(true)
            const timer = window.setTimeout(() => setJustSnapped(false), SNAP_ANIMATION_MS)
            return () => window.clearTimeout(timer)
        }, [position])

        const pushChange = useCallback(
            (newImageUrl: string | null, newText: string, newPosition: SignatureLogoPosition, newMaxWidth: number) => {
                const combined = buildContent(newImageUrl, newText, newPosition, newMaxWidth)
                isInternalChangeRef.current = true
                lastContentRef.current = combined
                onChange(combined)
            },
            [onChange]
        )

        const handleTextChange = useCallback(
            (html: string) => {
                // Ignore the editor's mount-time normalization onChange (fires before the user focuses/types) —
                // propagating it would push an empty text up and wipe the seeded signature + logo. The seeded
                // text arrives via the content-sync effect (setText), not through here.
                if (!textFocusedRef.current) return
                setText(html)
                pushChange(imageUrl, html, position, maxWidth)
            },
            [imageUrl, position, maxWidth, pushChange]
        )

        const handlePositionChange = useCallback(
            (next: SignatureLogoPosition) => {
                setPosition(next)
                pushChange(imageUrl, text, next, maxWidth)
            },
            [imageUrl, text, maxWidth, pushChange]
        )

        const handleSizeChange = useCallback(
            (next: number) => {
                setMaxWidth(next)
                pushChange(imageUrl, text, position, next)
            },
            [imageUrl, text, position, pushChange]
        )

        const cyclePosition = useCallback(() => {
            const i = POSITION_CYCLE.indexOf(position)
            const next = POSITION_CYCLE[(i + 1) % POSITION_CYCLE.length]
            handlePositionChange(next)
        }, [position, handlePositionChange])

        const handleImageClick = useCallback(() => {
            if (uploading) return
            fileInputRef.current?.click()
        }, [uploading])

        const handleFileChange = useCallback(
            async (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0]
                if (fileInputRef.current) fileInputRef.current.value = ''
                if (!file || !onImageUpload) return

                setUploading(true)
                try {
                    const url = await onImageUpload(file)
                    if (url) {
                        setImageUrl(url)
                        pushChange(url, text, position, maxWidth)
                    }
                } finally {
                    setUploading(false)
                }
            },
            [onImageUpload, pushChange, text, position, maxWidth]
        )

        const handleRemoveImage = useCallback(() => {
            setImageUrl(null)
            setIsLogoFocused(false)
            pushChange(null, text, position, maxWidth)
        }, [pushChange, text, position, maxWidth])

        const handleDragOver = useCallback((e: React.DragEvent) => {
            e.preventDefault()
            e.stopPropagation()
        }, [])

        const handleDrop = useCallback(
            async (e: React.DragEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (uploading || !onImageUpload) return
                const file = e.dataTransfer.files?.[0]
                if (!file || !file.type.startsWith('image/')) return

                setUploading(true)
                try {
                    const url = await onImageUpload(file)
                    if (url) {
                        setImageUrl(url)
                        pushChange(url, text, position, maxWidth)
                    }
                } finally {
                    setUploading(false)
                }
            },
            [uploading, onImageUpload, pushChange, text, position, maxWidth]
        )

        // Mouse-driven RESIZE: mousedown on the corner grip → track diagonal
        // drag distance, scale maxWidth live (which feeds the inline slot
        // dimensions), commit final value once on mouseup. A floating size
        // pill follows the cursor for precise readout. Tile grows 1:1.
        const handleResizeMouseDown = useCallback(
            (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (!imageUrl) return
                const startX = e.clientX
                const startY = e.clientY
                const startMaxWidth = maxWidth
                let liveMaxWidth = startMaxWidth

                setIsResizingLogo(true)
                setResizeBadgePos({ x: e.clientX, y: e.clientY })
                document.body.style.userSelect = 'none'
                document.body.style.cursor = 'nwse-resize'

                const onMove = (ev: MouseEvent) => {
                    const dx = ev.clientX - startX
                    const dy = ev.clientY - startY
                    // Diagonal drag: take the larger axis so either direction grows or shrinks the logo.
                    const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy
                    let next = Math.round(startMaxWidth + delta)
                    if (next < SIGNATURE_LOGO_MIN_MAX_WIDTH) next = SIGNATURE_LOGO_MIN_MAX_WIDTH
                    if (next > SIGNATURE_LOGO_MAX_MAX_WIDTH) next = SIGNATURE_LOGO_MAX_MAX_WIDTH
                    liveMaxWidth = next
                    setMaxWidth(next)
                    setResizeBadgePos({ x: ev.clientX, y: ev.clientY })
                }
                const cleanup = () => {
                    document.removeEventListener('mousemove', onMove)
                    document.removeEventListener('mouseup', onUp)
                    document.removeEventListener('keydown', onKey)
                    document.body.style.userSelect = ''
                    document.body.style.cursor = ''
                    setIsResizingLogo(false)
                    setResizeBadgePos(null)
                }
                const onUp = () => {
                    cleanup()
                    if (liveMaxWidth !== startMaxWidth) {
                        pushChange(imageUrl, text, position, liveMaxWidth)
                    }
                }
                const onKey = (ev: KeyboardEvent) => {
                    if (ev.key === 'Escape') {
                        setMaxWidth(startMaxWidth)
                        cleanup()
                    }
                }
                document.addEventListener('mousemove', onMove)
                document.addEventListener('mouseup', onUp)
                document.addEventListener('keydown', onKey)
            },
            [imageUrl, maxWidth, position, text, pushChange]
        )

        // Click-outside: deselect the logo when the user clicks anywhere
        // that isn't the logo column. Skipped while resizing — it cleans up
        // its own focus state.
        useEffect(() => {
            if (!isLogoFocused || isResizingLogo) return
            const handler = (e: MouseEvent) => {
                const target = e.target as Node
                if (columnRef.current?.contains(target)) return
                setIsLogoFocused(false)
            }
            document.addEventListener('mousedown', handler)
            return () => document.removeEventListener('mousedown', handler)
        }, [isLogoFocused, isResizingLogo])

        // Keyboard: arrows cycle position, +/- step the size by 10 px.
        const handleImageKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                if (!imageUrl) return
                let nextPosition: SignatureLogoPosition | null = null
                if (e.key === 'ArrowLeft') nextPosition = 'left'
                else if (e.key === 'ArrowRight') nextPosition = 'right'
                else if (e.key === 'ArrowUp') nextPosition = 'top'
                else if (e.key === 'ArrowDown') nextPosition = 'bottom'

                if (nextPosition) {
                    e.preventDefault()
                    if (nextPosition !== position) handlePositionChange(nextPosition)
                    return
                }

                if (e.key === '+' || e.key === '=') {
                    e.preventDefault()
                    const next = Math.min(maxWidth + SIZE_KEY_STEP, SIGNATURE_LOGO_MAX_MAX_WIDTH)
                    if (next !== maxWidth) handleSizeChange(next)
                    return
                }
                if (e.key === '-' || e.key === '_') {
                    e.preventDefault()
                    const next = Math.max(maxWidth - SIZE_KEY_STEP, SIGNATURE_LOGO_MIN_MAX_WIDTH)
                    if (next !== maxWidth) handleSizeChange(next)
                    return
                }
            },
            [imageUrl, position, maxWidth, handlePositionChange, handleSizeChange]
        )

        useImperativeHandle(
            ref,
            () => ({
                get editor() {
                    return editorRef.current?.editor || null
                },
                insertVariable: (variableName: string) => {
                    editorRef.current?.insertVariable(variableName)
                },
            }),
            []
        )

        // Tile dimensions track the configured logo size 1:1. The empty upload
        // tile keeps a stable click-target footprint so it doesn't disappear
        // when the user removes the image.
        const slotStyle = imageUrl ? { width: maxWidth + TILE_PADDING_TOTAL, height: maxWidth + TILE_PADDING_TOTAL } : undefined

        return (
            <div className="signature-footer-editor">
                <div className="signature-image-column" ref={columnRef}>
                    <div className="signature-image-slot" style={slotStyle}>
                        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleFileChange} className="hidden" />
                        {imageUrl ? (
                            <div
                                className={`image-preview ${isLogoFocused ? 'is-focused' : ''} ${justSnapped ? 'is-snapping' : ''}`}
                                onMouseDown={(e) => {
                                    // Don't steal mousedown from the remove button or resize grip.
                                    const t = e.target as HTMLElement
                                    if (t.closest('.image-remove-btn') || t.closest('.image-resize-handle')) return
                                    e.preventDefault()
                                    setIsLogoFocused(true)
                                }}
                                onKeyDown={handleImageKeyDown}
                                tabIndex={0}
                                role="button"
                                aria-label="Signature logo. Drag the bottom-right corner to resize. Use arrow keys to move, +/- to resize."
                            >
                                <img src={imageUrl} alt="Signature" draggable={false} />
                                <button
                                    type="button"
                                    className="image-remove-btn"
                                    onClick={handleRemoveImage}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    aria-label="Remove image"
                                    title="Remove image"
                                >
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M3 3L9 9M9 3L3 9" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
                                    </svg>
                                </button>
                                {isLogoFocused && (
                                    <span
                                        className="image-resize-handle"
                                        onMouseDown={handleResizeMouseDown}
                                        role="button"
                                        aria-label="Drag to resize logo"
                                        title="Drag to resize"
                                    >
                                        <ResizeHandleIcon />
                                    </span>
                                )}
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="image-upload-zone"
                                onClick={handleImageClick}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                disabled={uploading}
                            >
                                {uploading ? (
                                    <span className="uploading-text">Uploading…</span>
                                ) : (
                                    <>
                                        <UploadIcon />
                                        <span className="upload-label">Add Logo</span>
                                        <span className="upload-hint">Drag or click</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    {imageUrl && (
                        <button
                            type="button"
                            className="position-diagram"
                            onClick={cyclePosition}
                            aria-label={`Logo at ${POSITION_LABELS[position]}. Click to change position.`}
                            title="Click to change position"
                        >
                            <PositionDiagram position={position} />
                            <span className="position-diagram-label">{POSITION_LABEL_CAPITALIZED[position]}</span>
                        </button>
                    )}
                </div>
                <div className="signature-editor-slot">
                    <TiptapEditor
                        ref={editorRef}
                        content={text}
                        onChange={handleTextChange}
                        placeholder={placeholder}
                        onFocus={() => {
                            textFocusedRef.current = true
                            onFocus?.()
                        }}
                        minHeight={minHeight}
                    />
                </div>
                {isResizingLogo && resizeBadgePos && (
                    <div className="logo-size-badge" style={{ left: resizeBadgePos.x + 14, top: resizeBadgePos.y + 14 }} aria-hidden="true">
                        {maxWidth} px
                    </div>
                )}
            </div>
        )
    }
)

SignatureFooterEditor.displayName = 'SignatureFooterEditor'
